import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Loader2, Calendar, Users, Video, ExternalLink, Copy, Radio, Settings2, ChevronLeft, ChevronRight, Filter, SlidersHorizontal, MessagesSquare, Ban, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CAPS, hasCapability } from '../../utils/capabilities';
import MeetingDefaultsModal from './MeetingDefaultsModal';
import { FutureDatePicker } from '../ui/date-picker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grade catalog is fetched at runtime from /api/grades/config (see load()).
// If you need a static fallback list for tests, uncomment below.
// const GRADE_OPTIONS_FALLBACK = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];
const DURATION_OPTIONS = [30, 45, 60, 75, 90, 120, 150, 180];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// Nautical grades don't have MMD-specific exams — MMD only applies to
// engineering grades. Kept in sync with backend/routes/bulk_import.py.
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];

// Keep in sync with backend/models/enums.py MMDLocation
const MMD_LOCATIONS = [
  'Mumbai', 'Kolkata', 'Noida', 'Chennai', 'Kochi', 'Visakhapatnam',
  'Goa', 'Kandla', 'Mangalore', 'Tuticorin', 'Haldia', 'Paradip', 'Port Blair',
];

// Zoom meeting settings — the safer defaults for exam-prep. Admin can override.
const DEFAULT_MEETING_SETTINGS = {
  waiting_room: true,
  host_video: false,
  participant_video: false,
  mute_upon_entry: true,
  join_before_host: false,
  auto_recording: 'none',
};

const fmtIST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const statusColor = (s) => {
  switch (s) {
    case 'scheduled': return 'bg-blue-600 text-white';
    case 'live': return 'bg-red-600 text-white animate-pulse';
    case 'completed': return 'bg-gray-500 text-white';
    case 'cancelled': return 'bg-red-100 text-red-700 border border-red-300';
    default: return 'bg-gray-200 text-gray-700';
  }
};

const EMPTY_FORM = {
  title: '',
  description: '',
  grades: [],
  functions: [],
  mmd_locations: [],
  start_date: '',
  start_time_local: '',
  duration_minutes: 60,
  host_user_id: '',
  zoom_account_id: 'auto',
  qna_cutoff_hours: 24,
  settings: { ...DEFAULT_MEETING_SETTINGS },
};

const QNA_CUTOFF_OPTIONS = [6, 12, 24, 48, 72];

const buildISTIso = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  return `${dateStr}T${timeStr}:00+05:30`;
};

// Single settings row — kept at module scope so React reconciles it stably.
const SettingRow = ({ label, description, value, onChange, testId }) => (
  <div className="flex items-start justify-between py-2 gap-3">
    <div className="flex-1">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const LiveClassScheduler = ({ canSchedule = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data state
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hosts, setHosts] = useState([]);
  const [zoomAccounts, setZoomAccounts] = useState([]);

  // Form state
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Filters
  const [gradeFilter, setGradeFilter] = useState('all');   // 'all' | GRADE_OPTIONS value
  const [hostFilter, setHostFilter] = useState('all');     // 'all' | host_id
  const [showCompleted, setShowCompleted] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Grade + function catalogs (fetched from platform sources of truth)
  const [gradeCatalog, setGradeCatalog] = useState([]);          // [{ grade_name, category, ... }]
  const [functionCatalog, setFunctionCatalog] = useState([]);    // [{ id, grade, name, function_number, exam_type }]

  // Platform defaults (fetched once)
  const [platformDefaults, setPlatformDefaults] = useState(DEFAULT_MEETING_SETTINGS);
  const [showDefaultsModal, setShowDefaultsModal] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [cls, hostsResp, accResp, defaultsResp, gradesResp, functionsResp] = await Promise.all([
        axios.get(`${API}/admin/live-classes`),
        canSchedule ? axios.get(`${API}/admin/live-classes/hosts`) : Promise.resolve({ data: [] }),
        canSchedule ? axios.get(`${API}/admin/zoom-accounts`) : Promise.resolve({ data: [] }),
        canSchedule ? axios.get(`${API}/admin/live-classes/settings/defaults`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        axios.get(`${API}/grades/config`).catch(() => ({ data: { grades: [] } })),
        axios.get(`${API}/functions`).catch(() => ({ data: [] })),
      ]);
      setClasses(cls.data || []);
      setHosts(hostsResp.data || []);
      setZoomAccounts((accResp.data || []).filter(a => a.is_active));
      setGradeCatalog(gradesResp?.data?.grades || []);
      setFunctionCatalog(functionsResp.data || []);
      if (defaultsResp?.data?.defaults) setPlatformDefaults(defaultsResp.data.defaults);
    } catch (err) {
      console.error('Failed to load classes', err);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [gradeFilter, hostFilter, showCompleted, dateFrom, dateTo]);

  // Filter + sort (descending by start_time — most recent first)
  const filteredClasses = useMemo(() => {
    // date bounds: interpret as IST-local start-of-day / end-of-day
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00+05:30`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59+05:30`).getTime() : null;
    return classes
      .filter(c => showCompleted ? true : c.status !== 'completed')
      .filter(c => {
        if (gradeFilter === 'all') return true;
        return Array.isArray(c.grades) && c.grades.includes(gradeFilter);
      })
      .filter(c => hostFilter === 'all' ? true : c.host_user_id === hostFilter)
      .filter(c => {
        const t = new Date(c.start_time).getTime();
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
        return true;
      })
      .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  }, [classes, gradeFilter, hostFilter, showCompleted, dateFrom, dateTo]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredClasses.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedClasses = useMemo(
    () => filteredClasses.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredClasses, safePage, pageSize]
  );

  const activeFilterCount =
    (gradeFilter !== 'all' ? 1 : 0) +
    (hostFilter !== 'all' ? 1 : 0) +
    (showCompleted ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  const clearFilters = () => {
    setGradeFilter('all');
    setHostFilter('all');
    setShowCompleted(false);
    setDateFrom('');
    setDateTo('');
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, settings: { ...platformDefaults } });
    setShowSettings(false);
    setShowDialog(true);
  };

  const openEdit = (cls) => {
    setEditingId(cls.id);
    const dt = new Date(cls.start_time);
    const pad = (n) => String(n).padStart(2, '0');
    const ist = new Date(dt.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    setForm({
      title: cls.title,
      description: cls.description || '',
      grades: Array.isArray(cls.grades) ? cls.grades : [],
      functions: Array.isArray(cls.functions) ? cls.functions : [],
      mmd_locations: Array.isArray(cls.mmd_locations) ? cls.mmd_locations : [],
      start_date: `${ist.getFullYear()}-${pad(ist.getMonth() + 1)}-${pad(ist.getDate())}`,
      start_time_local: `${pad(ist.getHours())}:${pad(ist.getMinutes())}`,
      duration_minutes: cls.duration_minutes,
      host_user_id: cls.host_user_id,
      zoom_account_id: cls.zoom_account_id || 'auto',
      qna_cutoff_hours: typeof cls.qna_cutoff_hours === 'number' ? cls.qna_cutoff_hours : 24,
      settings: { ...DEFAULT_MEETING_SETTINGS, ...(cls.settings || {}) },
    });
    setShowSettings(false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.start_date || !form.start_time_local) { toast.error('Start date and time required'); return; }
    if (!form.host_user_id) { toast.error('Please pick a host'); return; }
    if (!form.grades || form.grades.length === 0) { toast.error('Pick at least one grade'); return; }
    const start_time = buildISTIso(form.start_date, form.start_time_local);
    if (!start_time) { toast.error('Invalid date/time'); return; }

    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        grades: form.grades,
        functions: form.functions,
        mmd_locations: form.mmd_locations || [],
        start_time,
        duration_minutes: form.duration_minutes,
        host_user_id: form.host_user_id,
        zoom_account_id: form.zoom_account_id === 'auto' ? null : form.zoom_account_id,
        qna_cutoff_hours: form.qna_cutoff_hours,
        settings: form.settings,
      };
      if (editingId) {
        await axios.put(`${API}/admin/live-classes/${editingId}`, payload);
        toast.success('Class updated');
      } else {
        await axios.post(`${API}/admin/live-classes`, payload);
        toast.success('Class scheduled — Zoom meeting created');
      }
      setShowDialog(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      // Zoom license conflict: detail is a dict with message + conflicts[]
      if (d && typeof d === 'object' && d.code && (d.code === 'zoom_license_conflict' || d.code === 'all_zoom_licenses_busy')) {
        setConflictDialog(d);
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to save class'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Zoom-license conflict modal state
  const [conflictDialog, setConflictDialog] = useState(null);

  // Cancel modal state
  const [cancelDialog, setCancelDialog] = useState(null); // { id, title } or null
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Hard-delete confirm state
  const [deleteDialog, setDeleteDialog] = useState(null); // { id, title } or null
  const [deleting, setDeleting] = useState(false);

  const openCancel = (id, title) => { setCancelDialog({ id, title }); setCancelReason(''); };
  const submitCancel = async () => {
    if (!cancelDialog) return;
    try {
      setCancelling(true);
      const body = cancelReason.trim() ? { cancellation_reason: cancelReason.trim() } : {};
      await axios.delete(`${API}/admin/live-classes/${cancelDialog.id}`, { data: body });
      toast.success('Class cancelled');
      setCancelDialog(null); setCancelReason('');
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to cancel class'));
    } finally {
      setCancelling(false);
    }
  };

  const openDelete = (id, title) => setDeleteDialog({ id, title });
  const submitDelete = async () => {
    if (!deleteDialog) return;
    try {
      setDeleting(true);
      await axios.delete(`${API}/admin/live-classes/${deleteDialog.id}/permanent`);
      toast.success('Class permanently deleted');
      setDeleteDialog(null);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to delete class'));
    } finally {
      setDeleting(false);
    }
  };

  // A class can be hard-deleted only strictly before the join window opens
  // (15 minutes before start_time). Cancelled classes are still eligible for
  // hard-delete if they never actually happened.
  const canHardDelete = (c) => {
    if (!c || c.status === 'completed' || c.status === 'live') return false;
    const start = new Date(c.start_time).getTime();
    const joinOpen = start - 15 * 60 * 1000;
    return Date.now() < joinOpen;
  };

  const canStartAsHost = (cls) => {
    if (!user) return false;
    const isAssignedHost = cls.host_user_id === user.id;
    // Full admin OR any sub-admin with manage_classes can host any class;
    // host_classes-only sub-admins must be the assigned host.
    const canHostAny = user.role === 'admin' || hasCapability(user, CAPS.MANAGE_CLASSES);
    if (!isAssignedHost && !canHostAny) return false;
    if (cls.status === 'cancelled' || cls.status === 'completed') return false;
    const now = Date.now();
    const start = new Date(cls.start_time).getTime();
    const end = start + (cls.duration_minutes + 30) * 60 * 1000;
    const openAt = start - 15 * 60 * 1000;
    return now >= openAt && now <= end;
  };

  const startAsHost = (classId) => navigate(`/admin/live-classes/${classId}/host`);
  const copyToClipboard = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));

  return (
    <>
      <Card data-testid="live-classes-scheduler-card">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {canSchedule ? 'Scheduled Classes' : 'My Assigned Classes'}
              </CardTitle>
              <CardDescription className="mt-2">
                {canSchedule
                  ? 'Schedule instructor-led sessions. A Zoom meeting is created via S2S OAuth on save.'
                  : 'Classes where you are assigned as the host.'}
              </CardDescription>
            </div>
            {canSchedule && (
              <div className="flex items-center gap-2">
                <Button onClick={() => setShowDefaultsModal(true)} variant="outline" size="sm"
                  data-testid="live-classes-defaults-btn">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Defaults
                </Button>
                <Button onClick={openCreate} data-testid="live-classes-schedule-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Class
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 pb-4 mb-4 border-b">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Filter className="w-3.5 h-3.5" />
              <span className="font-semibold uppercase tracking-wide">Filters</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">{activeFilterCount}</Badge>
              )}
            </div>

            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="scheduler-filter-grade">
                <SelectValue placeholder="Grade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {gradeCatalog.map(g => {
                  const gname = g.grade_name || g.grade_key || g.name;
                  if (!gname) return null;
                  return <SelectItem key={gname} value={gname}>{gname}</SelectItem>;
                })}
              </SelectContent>
            </Select>

            <Select value={hostFilter} onValueChange={setHostFilter}>
              <SelectTrigger className="h-9 w-52" data-testid="scheduler-filter-host">
                <SelectValue placeholder="Host" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All hosts</SelectItem>
                {hosts.map(h => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name} <span className="text-xs text-gray-500">({h.role === 'admin' ? 'Admin' : 'Sub-admin'})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1.5 pl-2 border-l">
              <Label htmlFor="date-from" className="text-xs text-gray-500 whitespace-nowrap">From</Label>
              <Input id="date-from" type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-36" data-testid="scheduler-filter-date-from" />
              <Label htmlFor="date-to" className="text-xs text-gray-500 whitespace-nowrap ml-1">To</Label>
              <Input id="date-to" type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="h-9 w-36" data-testid="scheduler-filter-date-to" />
            </div>

            <div className="flex items-center gap-2 pl-2 border-l">
              <Switch
                id="scheduler-show-completed"
                checked={showCompleted}
                onCheckedChange={setShowCompleted}
                data-testid="scheduler-show-completed-switch"
              />
              <Label htmlFor="scheduler-show-completed" className="text-xs whitespace-nowrap cursor-pointer">
                Show completed
              </Label>
            </div>

            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}
                className="h-8 text-xs" data-testid="scheduler-clear-filters">
                Clear all
              </Button>
            )}

            <div className="ml-auto text-xs text-gray-500">
              {filteredClasses.length} {filteredClasses.length === 1 ? 'class' : 'classes'}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredClasses.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="live-classes-empty">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No classes match your filters.</p>
              {activeFilterCount > 0 && (
                <Button variant="link" size="sm" onClick={clearFilters} className="mt-1">Clear filters</Button>
              )}
              {activeFilterCount === 0 && canSchedule && (
                <p className="text-sm mt-1">Click &ldquo;Schedule Class&rdquo; to add one.</p>
              )}
            </div>
          ) : (
            <>
              <Table data-testid="live-classes-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Start (IST)</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Grades</TableHead>
                    <TableHead>License</TableHead>
                    <TableHead>Zoom Meeting</TableHead>
                    <TableHead>Status</TableHead>
                    {canSchedule && <TableHead className="text-right">Actions</TableHead>}
                    {!canSchedule && <TableHead className="text-right">Start</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedClasses.map(c => (
                    <TableRow key={c.id} data-testid={`live-class-row-${c.id}`}>
                      <TableCell className="font-medium">{c.title}</TableCell>
                      <TableCell className="text-sm">{fmtIST(c.start_time)}</TableCell>
                      <TableCell className="text-sm">{c.duration_minutes} min</TableCell>
                      <TableCell className="text-sm">{c.host_user_name}</TableCell>
                      <TableCell className="text-xs">
                        {Array.isArray(c.grades) && c.grades.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {c.grades.map(g => (
                              <span key={g} className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-medium text-gray-700 whitespace-nowrap">{g}</span>
                            ))}
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{c.zoom_account_label || '—'}</TableCell>
                      <TableCell>
                        {c.zoom_meeting_id ? (
                          <div className="flex items-center gap-1 text-xs">
                            <span className="font-mono">{c.zoom_meeting_id}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(String(c.zoom_meeting_id))}
                              data-testid={`live-class-copy-${c.id}`}>
                              <Copy className="w-3 h-3" />
                            </Button>
                            {c.zoom_join_url && (
                              <a href={c.zoom_join_url} target="_blank" rel="noreferrer" className="text-blue-600">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColor(c.status)}>{c.status}</Badge>
                      </TableCell>
                      {canSchedule && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 items-center">
                            {canStartAsHost(c) && (
                              <Button size="sm" onClick={() => startAsHost(c.id)}
                                className="bg-red-600 hover:bg-red-700 text-white mr-1"
                                data-testid={`live-class-start-${c.id}`}>
                                <Radio className="w-3 h-3 mr-1" />
                                Start
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/live-classes/${c.id}/questions`, { state: { from: '/admin/live-classes' } })}
                              className="text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                              title="Pre-class Q&A digest"
                              data-testid={`live-class-qna-${c.id}`}>
                              <MessagesSquare className="w-4 h-4" />
                              {c.qna_count > 0 && (
                                <span className="ml-1 text-[10px] font-semibold tabular-nums">{c.qna_count}</span>
                              )}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                              disabled={c.status === 'cancelled' || c.status === 'completed'}
                              data-testid={`live-class-edit-${c.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {c.status !== 'cancelled' && c.status !== 'completed' && (
                              <Button size="sm" variant="ghost" onClick={() => openCancel(c.id, c.title)}
                                title="Cancel class (keeps record + reason)"
                                data-testid={`live-class-cancel-${c.id}`}>
                                <Ban className="w-4 h-4 text-amber-600" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openDelete(c.id, c.title)}
                              disabled={!canHardDelete(c)}
                              title={canHardDelete(c)
                                ? 'Permanently delete this class (and its Q&A). Only allowed before the join window opens.'
                                : 'Delete unavailable — the join window has already opened. Cancel instead.'}
                              data-testid={`live-class-delete-${c.id}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      {!canSchedule && (
                        <TableCell className="text-right">
                          {canStartAsHost(c) ? (
                            <Button size="sm" onClick={() => startAsHost(c.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`live-class-start-${c.id}`}>
                              <Radio className="w-3 h-3 mr-1" />
                              Start Class
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-400">Not yet</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination — always shown so it's discoverable */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t flex-wrap gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600" data-testid="scheduler-pagination-info">
                  <span>Rows per page</span>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                    <SelectTrigger className="h-8 w-20" data-testid="scheduler-page-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="ml-3">
                    Showing <strong>{filteredClasses.length === 0 ? 0 : (safePage - 1) * pageSize + 1}</strong>&ndash;
                    <strong>{Math.min(safePage * pageSize, filteredClasses.length)}</strong> of{' '}
                    <strong>{filteredClasses.length}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage <= 1} data-testid="scheduler-prev-page">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Prev
                  </Button>
                  <span className="text-xs px-3 tabular-nums whitespace-nowrap">Page {safePage} of {totalPages}</span>
                  <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages} data-testid="scheduler-next-page">
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      {canSchedule && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                {editingId ? 'Edit Class' : 'Schedule New Class'}
              </DialogTitle>
              <DialogDescription>
                All times are in IST (Asia/Kolkata). Meeting settings default to safer &ldquo;exam-prep&rdquo; mode &mdash; toggle them below to customise.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. MMD Function 3 Live Discussion"
                  data-testid="live-class-form-title" />
              </div>
              <div>
                <Label>Description / Agenda</Label>
                <Textarea rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional details visible to candidates"
                  data-testid="live-class-form-description" />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label>Grades <span className="text-red-500">*</span></Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="live-class-form-grades">
                    {gradeCatalog.length === 0 ? (
                      <p className="text-xs text-gray-500">No grades configured. Ask a super-admin to seed the Grade Config.</p>
                    ) : gradeCatalog.map(g => {
                      const gname = g.grade_name || g.grade_key || g.name;
                      if (!gname) return null;
                      const active = form.grades.includes(gname);
                      return (
                        <button
                          type="button"
                          key={gname}
                          onClick={() => setForm(f => ({
                            ...f,
                            grades: active ? f.grades.filter(x => x !== gname) : [...f.grades, gname],
                            // If a grade is removed, drop functions that only belonged to it.
                            functions: active
                              ? f.functions.filter(fid => {
                                  const fn = functionCatalog.find(fc => fc.id === fid);
                                  return fn && f.grades.filter(x => x !== gname).includes(fn.grade);
                                })
                              : f.functions,
                          }))}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active
                            ? 'bg-[#0A7EA4] text-white border-[#0A7EA4]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#0A7EA4] hover:text-[#0A7EA4]'}`}
                          data-testid={`live-class-form-grade-chip-${gname.replace(/\s+/g, '-')}`}
                        >
                          {gname}
                          {g.category && (
                            <span className={`ml-1 text-[9px] uppercase tracking-wide ${active ? 'text-white/70' : 'text-gray-400'}`}>
                              {g.category === 'Engineering' ? 'Eng' : g.category === 'Deck' ? 'Deck' : g.category}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    Users only see classes tagged with their chosen grade — pick every grade this class serves.
                  </p>
                </div>

                {/* Functions — filtered by selected grades */}
                <div>
                  <Label>Functions covered <span className="text-gray-400 text-xs font-normal">(optional)</span></Label>
                  {form.grades.length === 0 ? (
                    <p className="text-xs text-gray-500 mt-1.5">Pick at least one grade to see available functions.</p>
                  ) : (() => {
                    const availableFns = functionCatalog.filter(fn => form.grades.includes(fn.grade));
                    if (availableFns.length === 0) {
                      return <p className="text-xs text-gray-500 mt-1.5">No functions configured for the selected grade(s).</p>;
                    }
                    // Split by exam type — students prep differently for orals vs written
                    const orals = availableFns.filter(fn => (fn.exam_type || '').toLowerCase().startsWith('oral'));
                    const written = availableFns.filter(fn => (fn.exam_type || '').toLowerCase() === 'written');
                    const other = availableFns.filter(fn => !orals.includes(fn) && !written.includes(fn));

                    const renderChip = (fn) => {
                      const active = form.functions.includes(fn.id);
                      const isOrals = (fn.exam_type || '').toLowerCase().startsWith('oral');
                      const activeCls = isOrals
                        ? 'bg-[#0A7EA4] text-white border-[#0A7EA4]'
                        : (fn.exam_type || '').toLowerCase() === 'written'
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-gray-600 text-white border-gray-600';
                      return (
                        <button
                          type="button"
                          key={fn.id}
                          onClick={() => setForm(f => ({
                            ...f,
                            functions: active ? f.functions.filter(x => x !== fn.id) : [...f.functions, fn.id],
                          }))}
                          className={`px-2 py-1 rounded text-[11px] font-medium border transition-colors text-left ${active
                            ? activeCls
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}
                          data-testid={`live-class-form-function-chip-${fn.id}`}
                          title={`${fn.grade} · ${fn.exam_type || 'general'}`}
                        >
                          <span className="opacity-60 mr-1">F{fn.function_number}</span>
                          {fn.name}
                          <span className="ml-1 text-[9px] uppercase tracking-wide opacity-70">
                            {fn.grade.replace('MEO Class ', 'C')}
                          </span>
                        </button>
                      );
                    };

                    return (
                      <div className="mt-1.5 max-h-[280px] overflow-y-auto p-2 border rounded space-y-2" data-testid="live-class-form-functions">
                        {[
                          { key: 'orals', label: 'Orals', items: orals },
                          { key: 'written', label: 'Written', items: written },
                          { key: 'other', label: 'Other', items: other },
                        ].filter(g => g.items.length > 0).map(g => (
                          <div key={g.key} data-testid={`live-class-form-function-group-${g.key}`}>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                              {g.label} <span className="text-gray-400 font-normal">({g.items.length})</span>
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {g.items.map(renderChip)}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* MMD locations — optional targeting. Empty = visible to all users in the selected grades.
                    Only shown when at least one engineering grade is selected (nautical grades don't have
                    MMD-specific exams). If admin removes all engineering grades, we auto-clear any MMDs. */}
                {(() => {
                  const hasEngineeringGrade = form.grades.some(g => !NAUTICAL_GRADES.includes(g));
                  if (!hasEngineeringGrade) {
                    // Silently clear MMDs if only nautical grades remain
                    if ((form.mmd_locations || []).length > 0) {
                      // Defer state update to avoid React setState-in-render warning
                      setTimeout(() => setForm(f => ({ ...f, mmd_locations: [] })), 0);
                    }
                    if (form.grades.length === 0) return null;
                    return (
                      <div data-testid="live-class-form-mmds-nautical-hint">
                        <Label>MMD locations <span className="text-gray-400 text-xs font-normal">(not applicable)</span></Label>
                        <p className="text-[11px] text-gray-500 mt-1.5">
                          MMD targeting doesn&apos;t apply to nautical grades ({NAUTICAL_GRADES.join(', ')}). Add an engineering grade above to enable MMD filtering.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <Label>
                        MMD locations{' '}
                        <span className="text-gray-400 text-xs font-normal">(optional — leave empty for everyone)</span>
                      </Label>
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="live-class-form-mmds">
                        {MMD_LOCATIONS.map((m) => {
                          const active = (form.mmd_locations || []).includes(m);
                          return (
                            <button
                              type="button"
                              key={m}
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  mmd_locations: active
                                    ? (f.mmd_locations || []).filter((x) => x !== m)
                                    : [...(f.mmd_locations || []), m],
                                }))
                              }
                              className={`px-2 py-0.5 rounded text-[11px] font-medium border transition-colors ${
                                active
                                  ? 'bg-slate-700 text-white border-slate-700'
                                  : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
                              }`}
                              data-testid={`live-class-form-mmd-chip-${m}`}
                            >
                              {m}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        When set, only users whose profile MMD matches one of these will see the class. Users without an MMD set won&apos;t see MMD-tagged classes.
                      </p>
                    </div>
                  );
                })()}

                <div>
                  <Label>Duration</Label>
                  <Select value={String(form.duration_minutes)}
                    onValueChange={(v) => setForm({ ...form, duration_minutes: parseInt(v) })}>
                    <SelectTrigger data-testid="live-class-form-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map(d => <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date *</Label>
                  <FutureDatePicker
                    date={form.start_date ? new Date(`${form.start_date}T00:00:00`) : null}
                    onDateChange={(d) => {
                      if (!d) { setForm(f => ({ ...f, start_date: '' })); return; }
                      const pad = (n) => String(n).padStart(2, '0');
                      const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                      setForm(f => ({ ...f, start_date: iso }));
                    }}
                    placeholder="Pick a date"
                    className="mt-1"
                  />
                  {/* Hidden mirror for tests / testid stability */}
                  <input type="hidden" data-testid="live-class-form-date" value={form.start_date} readOnly />
                </div>
                <div>
                  <Label>Start Time (IST) *</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Select
                      value={form.start_time_local ? form.start_time_local.split(':')[0] : ''}
                      onValueChange={(hh) => setForm(f => {
                        const mm = (f.start_time_local && f.start_time_local.split(':')[1]) || '00';
                        return { ...f, start_time_local: `${hh}:${mm}` };
                      })}
                    >
                      <SelectTrigger className="w-full" data-testid="live-class-form-hour">
                        <SelectValue placeholder="HH" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[240px]">
                        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-gray-500">:</span>
                    <Select
                      value={form.start_time_local ? form.start_time_local.split(':')[1] : ''}
                      onValueChange={(mm) => setForm(f => {
                        const hh = (f.start_time_local && f.start_time_local.split(':')[0]) || '00';
                        return { ...f, start_time_local: `${hh}:${mm}` };
                      })}
                    >
                      <SelectTrigger className="w-full" data-testid="live-class-form-minute">
                        <SelectValue placeholder="MM" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[240px]">
                        {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <input type="hidden" data-testid="live-class-form-time" value={form.start_time_local} readOnly />
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Users className="w-4 h-4" /> Host *</Label>
                <Select value={form.host_user_id} onValueChange={(v) => setForm({ ...form, host_user_id: v })}>
                  <SelectTrigger data-testid="live-class-form-host">
                    <SelectValue placeholder="Pick an admin or sub-admin" />
                  </SelectTrigger>
                  <SelectContent>
                    {hosts.map(h => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name} ({h.role === 'admin' ? 'Admin' : 'Sub-admin'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Video className="w-4 h-4" /> Zoom License</Label>
                <Select value={form.zoom_account_id} onValueChange={(v) => setForm({ ...form, zoom_account_id: v })}>
                  <SelectTrigger data-testid="live-class-form-license">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-assign (least recently used)</SelectItem>
                    {zoomAccounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {zoomAccounts.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No active Zoom license configured. Add one first.</p>
                )}
              </div>

              {/* Pre-class Q&A cutoff */}
              <div>
                <Label className="flex items-center gap-1">
                  <MessagesSquare className="w-4 h-4" /> Q&amp;A window closes
                </Label>
                <Select
                  value={String(form.qna_cutoff_hours)}
                  onValueChange={(v) => setForm({ ...form, qna_cutoff_hours: parseInt(v) })}
                >
                  <SelectTrigger data-testid="live-class-form-qna-cutoff">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QNA_CUTOFF_OPTIONS.map(h => (
                      <SelectItem key={h} value={String(h)}>{h} hours before class</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Candidates can post &amp; upvote questions until this cutoff. Use the digest icon on the class row to consolidate them before class.
                </p>
              </div>

              {/* Meeting settings — collapsible */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowSettings(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50"
                  data-testid="live-class-form-settings-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">Meeting settings</span>
                    <Badge variant="outline" className="text-xs">
                      {Object.entries(form.settings).filter(([k, v]) => v !== platformDefaults[k]).length === 0
                        ? 'Defaults' : 'Customised'}
                    </Badge>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showSettings ? 'rotate-90' : ''}`} />
                </button>
                {showSettings && (
                  <div className="px-3 pb-3 pt-1 divide-y divide-gray-100" data-testid="live-class-form-settings-panel">
                    <SettingRow
                      label="Waiting room"
                      description="Attendees wait in a lobby until the host admits them."
                      value={form.settings.waiting_room}
                      onChange={(v) => setForm({ ...form, settings: { ...form.settings, waiting_room: v } })}
                      testId="setting-waiting-room"
                    />
                    <SettingRow
                      label="Mute participants on entry"
                      description="Attendees join muted; they can unmute themselves."
                      value={form.settings.mute_upon_entry}
                      onChange={(v) => setForm({ ...form, settings: { ...form.settings, mute_upon_entry: v } })}
                      testId="setting-mute-entry"
                    />
                    <SettingRow
                      label="Turn OFF participant video on entry"
                      description="Attendees join with camera off. They can still turn it on themselves unless disabled at the Zoom account level."
                      value={!form.settings.participant_video}
                      onChange={(v) => setForm({ ...form, settings: { ...form.settings, participant_video: !v } })}
                      testId="setting-participant-video"
                    />
                    <SettingRow
                      label="Turn OFF host video on entry"
                      description="Host joins with their own camera off (can turn on manually)."
                      value={!form.settings.host_video}
                      onChange={(v) => setForm({ ...form, settings: { ...form.settings, host_video: !v } })}
                      testId="setting-host-video"
                    />
                    <SettingRow
                      label="Allow participants to join before host"
                      description="If OFF, the meeting doesn't start until the host launches it."
                      value={form.settings.join_before_host}
                      onChange={(v) => setForm({ ...form, settings: { ...form.settings, join_before_host: v } })}
                      testId="setting-join-before-host"
                    />
                    <div className="flex items-start justify-between py-2 gap-3">
                      <div className="flex-1">
                        <Label className="text-sm font-medium">Auto recording</Label>
                        <p className="text-xs text-gray-500 mt-0.5">Automatically record the session (cloud recording requires a paid Zoom add-on).</p>
                      </div>
                      <Select value={form.settings.auto_recording}
                        onValueChange={(v) => setForm({ ...form, settings: { ...form.settings, auto_recording: v } })}>
                        <SelectTrigger className="w-32 h-9" data-testid="setting-auto-recording">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Off</SelectItem>
                          <SelectItem value="local">Local</SelectItem>
                          <SelectItem value="cloud">Cloud</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded text-[11px] text-blue-800 leading-snug">
                      <strong>Tip:</strong> To prevent participants from turning on their cameras during the class, the host clicks <em>Security &rarr; Suspend Participant Activities</em> inside the Zoom meeting. Zoom&apos;s schedule API doesn&apos;t expose this as a pre-configurable lock.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || zoomAccounts.length === 0}
                className="flex-1" data-testid="live-class-form-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{editingId ? 'Updating…' : 'Scheduling…'}</> :
                  (editingId ? 'Update Class' : 'Schedule Class')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Platform defaults modal */}
      {canSchedule && (
        <MeetingDefaultsModal
          open={showDefaultsModal}
          onOpenChange={setShowDefaultsModal}
          onSaved={(newDefaults) => setPlatformDefaults(newDefaults)}
        />
      )}

      {/* Cancel class modal — optional reason */}
      <Dialog open={!!cancelDialog} onOpenChange={(v) => { if (!v) { setCancelDialog(null); setCancelReason(''); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="cancel-class-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-amber-600" />
              Cancel class
            </DialogTitle>
            <DialogDescription>
              Cancel <strong>{cancelDialog?.title}</strong>? The record stays on the platform for
              transparency. Students will see this class marked as cancelled, along with your
              reason (if you share one).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 mt-2">
            <Label htmlFor="cancel-reason" className="text-sm">
              Reason for cancellation
              <span className="text-xs text-gray-400 font-normal ml-1">(optional, shown to students)</span>
            </Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Faculty on emergency leave. We'll reschedule this session soon."
              className="min-h-[90px] text-sm"
              maxLength={500}
              data-testid="cancel-reason-textarea"
            />
            <div className="text-right text-[10px] text-gray-400 tabular-nums">{cancelReason.length}/500</div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => { setCancelDialog(null); setCancelReason(''); }} disabled={cancelling}>
              Keep it
            </Button>
            <Button onClick={submitCancel} disabled={cancelling}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="cancel-class-confirm-btn">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel class'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hard-delete confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={(v) => { if (!v) setDeleteDialog(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="delete-class-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete permanently
            </DialogTitle>
            <DialogDescription>
              This will <strong>permanently remove</strong> &ldquo;<strong>{deleteDialog?.title}</strong>&rdquo; from the
              database — including all its pre-class Q&amp;A. The Zoom meeting will be cancelled too.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Use <strong>Cancel</strong> instead if you want to keep the record visible (recommended). Hard-delete is only allowed before the join window opens.</span>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setDeleteDialog(null)} disabled={deleting}>
              Keep it
            </Button>
            <Button onClick={submitDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-class-confirm-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Zoom license conflict dialog */}
      <Dialog open={!!conflictDialog} onOpenChange={(v) => { if (!v) setConflictDialog(null); }}>
        <DialogContent data-testid="zoom-conflict-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Zoom License Conflict
            </DialogTitle>
            <DialogDescription>{conflictDialog?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {conflictDialog?.code === 'zoom_license_conflict' && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-medium text-amber-900 mb-2">
                  License: {conflictDialog?.license_label || conflictDialog?.license_id}
                </p>
                <ul className="space-y-1.5" data-testid="zoom-conflict-list">
                  {(conflictDialog?.conflicts || []).map((c) => (
                    <li key={c.id} className="text-xs text-amber-900 flex justify-between gap-3">
                      <span className="font-medium truncate">{c.title || 'Untitled class'}</span>
                      <span className="tabular-nums whitespace-nowrap text-amber-700">{fmtIST(c.start_time)} · {c.duration_minutes}m</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {conflictDialog?.code === 'all_zoom_licenses_busy' && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <p className="text-xs font-medium text-red-900 mb-2">All active licenses busy at this time:</p>
                <ul className="space-y-2" data-testid="zoom-all-busy-list">
                  {(conflictDialog?.licenses || []).map((lic) => (
                    <li key={lic.license_id} className="text-xs">
                      <p className="font-medium text-red-900">{lic.license_label || lic.license_id}</p>
                      <ul className="ml-3 mt-1 space-y-0.5">
                        {(lic.conflicts || []).map((c) => (
                          <li key={c.id} className="text-red-800 flex justify-between gap-3">
                            <span className="truncate">{c.title || 'Untitled'}</span>
                            <span className="tabular-nums whitespace-nowrap">{fmtIST(c.start_time)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-500">
              A 15-minute buffer is enforced between back-to-back classes on the same license to allow for overruns.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setConflictDialog(null)} data-testid="zoom-conflict-close-btn">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LiveClassScheduler;
